import { DropOverlay } from "@/components/common/DropOverlay/DropOverlay"
import { EmptyState } from "@/components/common/EmptyState/EmptyState"
import { FileUploadIllustration } from "@/components/common/FileUploadIllustration/FileUploadIllustration"
import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { HelpdeskLink } from "@/features/helpdesk"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { cn } from "@/lib/utils"
import { FolderOpen } from "lucide-react"
import { useRef } from "react"
import { useNavigate } from "react-router"
import {
  DECLARATION_ACCEPTED_EXTENSIONS,
  DECLARATION_FILE_INPUT_ACCEPT,
} from "../declarationFileTypes"
import { ImportRowList } from "../ImportRowList"
import { DECLARATIONS_BASE } from "../routes"
import { useDeclarationImport } from "../useDeclarationImport"

// One upload, both formats — there is deliberately no separate "import Excel"
// affordance. FileUploadIllustration requires exactly 2 format badges.
const DECL_FORMATS: [
  { label: string; colorClass: string },
  { label: string; colorClass: string },
] = [
  { label: "PDF", colorClass: "text-[#E06C75]" },
  { label: "XLSX", colorClass: "text-success" },
]

/**
 * Full-screen upload page. IT TAKES A FOLDER, NOT A FILE: the picker is
 * `multiple` and the drop overlay's whole accepted set is imported, where both
 * paths used to keep `files[0]` and silently discard the rest.
 *
 * The per-file result list is the point of the screen rather than a decoration.
 * A real intake is mixed — PDFs, workbooks, and the Word documents this flow does
 * not read yet — and each refusal carries a specific, useful sentence from the
 * server. Collapsing that into "12 of 50 failed" would throw away the only part
 * the user can act on, so every file keeps its own row and its own reason, and
 * the page stays on them for as long as there IS a refusal to read. A run where
 * every file made it has nothing to say that the overview doesn't already show,
 * so that case returns there directly.
 */
export function DeclarationImportPage() {
  const { translate } = useTranslation()
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)
  // The intake itself lives in `useDeclarationImport` (shared with the
  // overview's drop zone); this page adds the picker, the result list and the
  // "clean run goes straight back" navigation.
  const { rows, isImporting, importFiles, reset } = useDeclarationImport()
  // Upload path is a plain async fn, not a guarded hook — hide the button
  // directly so users without upload rights don't see the action at all.
  const { hasPermission } = useUser()
  const canUpload = hasPermission(PERMISSIONS.MC_DECLARATIONS_UPLOAD)

  const handleFiles = async (files: File[]) => {
    const outcome = await importFiles(files)
    // The result list earns its screen only when it has refusals to explain. A
    // fully clean import returns straight to the overview, where the new rows
    // already show as processing; the hook's toast carries the confirmation.
    if (outcome && outcome.failed === 0) navigate(DECLARATIONS_BASE)
  }

  const doneCount = rows.filter((r) => r.state === "done").length
  const failedCount = rows.filter((r) => r.state === "failed").length
  const finished = rows.length > 0 && !isImporting

  if (rows.length > 0) {
    return (
      <div className="flex h-full flex-col gap-4 p-6">
        <div className="flex items-center justify-between gap-4">
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-medium text-white">
              {isImporting
                ? translate("MC_DECL_IMPORT_RUNNING", {
                    done: doneCount + failedCount,
                    total: rows.length,
                  })
                : translate("MC_DECL_IMPORT_SUMMARY", {
                    uploaded: doneCount,
                    failed: failedCount,
                  })}
            </h2>
            {finished && doneCount > 0 && (
              <p className="text-[13px] text-grey5">
                {translate("MC_DECL_IMPORT_EXTRACTION_RUNNING")}
              </p>
            )}
          </div>
          <div className="flex shrink-0 gap-2">
            {finished && (
              <Button
                variant="secondary"
                onClick={() => {
                  reset()
                  inputRef.current?.click()
                }}
              >
                {translate("MC_DECL_BTN_IMPORT_ANOTHER")}
              </Button>
            )}
            <Button
              disabled={isImporting}
              onClick={() => navigate(DECLARATIONS_BASE)}
            >
              {translate("MC_DECL_BTN_BACK_DECLARATIONS")}
            </Button>
          </div>
        </div>

        <ImportRowList rows={rows} className="flex-1" />
        <input
          ref={inputRef}
          type="file"
          accept={DECLARATION_FILE_INPUT_ACCEPT}
          multiple
          className="hidden"
          onChange={(e) => {
            void handleFiles(Array.from(e.target.files ?? []))
            e.target.value = ""
          }}
        />
      </div>
    )
  }

  return (
    <div data-agent-target="suppliers.declaration-upload" className="h-full">
      <EmptyState
        illustration={<FileUploadIllustration formats={DECL_FORMATS} />}
        title={translate("MC_DECL_UPLOAD_DROP_TITLE")}
        description={
          <>
            {translate("MC_DECL_UPLOAD_DROP_SUBTITLE")}
            <br />
            <span className="text-xs text-muted-foreground">
              {translate("MC_DECL_V2_IMPORT_VALUE_PROP")}
            </span>
            <br />
            <HelpdeskLink
              categoryId="supplier-studio"
              articleId="erklaerungen-importieren"
              className="mt-2"
            />
          </>
        }
        hoverable
        className={cn("h-full")}
        onClose={() => navigate(DECLARATIONS_BASE)}
        primaryAction={
          canUpload
            ? {
                label: translate("MC_DECL_BTN_CHOOSE_FILES"),
                icon: <FolderOpen size={14} />,
                onClick: () => inputRef.current?.click(),
              }
            : undefined
        }
      />
      <input
        ref={inputRef}
        type="file"
        accept={DECLARATION_FILE_INPUT_ACCEPT}
        multiple
        className="hidden"
        onChange={(e) => {
          void handleFiles(Array.from(e.target.files ?? []))
          e.target.value = ""
        }}
      />
      <DropOverlay
        acceptedExtensions={DECLARATION_ACCEPTED_EXTENSIONS}
        hint={translate("MC_DECL_DROP_HINT")}
        onFilesDropped={() => {}}
        // The WHOLE drop, in one call: this page reports per file, so the accepted
        // and rejected halves have to arrive together — the rejected ones get a row
        // naming them and the reason, instead of one toast that names none of them.
        onAllFilesDropped={(files) => void handleFiles(files)}
      />
    </div>
  )
}
